import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import {
    HttpRequestOpts,
    HttpRequestError,
    HttpRequestResult,
    InternalHttpRequestError,
    AxiosHttpRequestError
} from "./types";
import { ApiErrorResponse, ApiResponse } from "@/shared/types/http";
import { getMessage, getMessageKeyFromRequestUrl, messageExists } from "./error-messages";
import { useGeneralStore } from "@/store/general.store";
import { logUserOut, refreshAuthData } from "../auth.service";
import { AddMessageInput, displayMessage } from "@/store/message.store";
import { useAuthStore } from "@/store/auth.store";
import momentTz from "@/helpers/moment";

const AXIOS_INSTANCE: AxiosInstance = axios.create();

async function getUserAccessToken(): Promise<undefined | string> {
    const AUTH_STORE = useAuthStore();
    if (!AUTH_STORE.is_logged_in || !AUTH_STORE.auth_data) return undefined;

    const THRESHOLD = Date.now() + 2 * 60 * 1000;
    // Sprawdzenie wygaśnięcia tokenów
    if (momentTz(AUTH_STORE.auth_data.refresh_token_exp_date).valueOf() <= THRESHOLD) {
        return undefined;
    }
    // Jeśli access token wygasł, ale refresh token nadal ważny – odświeżamy token
    if (momentTz(AUTH_STORE.auth_data.access_token_exp_date).valueOf() < THRESHOLD) {
        try {
            await refreshAuthData(AUTH_STORE.auth_data);
        } catch (err) {
            console.error(err);
            return undefined;
        }
    }

    return AUTH_STORE.auth_data.access_token;
}

/*####################
### REQUEST METHOD ###
####################*/
export async function httpRequest<T = ApiResponse>(
    request_config: AxiosRequestConfig,
    opts: Partial<HttpRequestOpts> = {}
): Promise<HttpRequestResult<T>> {
    const GENERAL_STORE = useGeneralStore();

    // 1. Konfiguracja
    let config: AxiosRequestConfig = { ...request_config };

    if (!opts.handle_unauthorized) opts.handle_unauthorized = "logout_user";
    if (!opts.auth) opts.auth = "force";

    // 2. Jeśli URL jest relatywny i prepend_api_path nie jest wyłączone, dodajemy bazowy path
    const base_api_url = GENERAL_STORE.API_BASE_PATH || "";
    if (config.url && config.url.startsWith("/") && opts.prepend_api_path !== false) {
        config.url = base_api_url + config.url;
    }

    // 3. Jeśli zapytanie kierowane jest do naszego API, sprawdzamy autoryzację
    if (config.url && config.url.startsWith(GENERAL_STORE.API_BASE_URL) && opts.auth !== "never") {
        // 3.1. Czekamy, aż aplikacja się załaduje
        if (opts.await_for_app_boot !== false) {
            await GENERAL_STORE.app_boot_promise;
        }

        // 3.2. Pobieramy access token użytkownika (z opcjonalnym odświeżeniem)
        const ACCESS_TOKEN = await getUserAccessToken();

        // 3.3. Jeżeli nie ma tokenu, a jest wymagany to throw
        if (!ACCESS_TOKEN && opts.auth === "force") {
            if (opts.handle_unauthorized === "logout_user") {
                await logUserOut({
                    alert_msg:
                        "Twoja sesja wygasła. Zaloguj się ponownie, aby kontynuować korzystanie z aplikacji",
                    alert_type: "error"
                });
            }
            throw {
                type: "internal",
                msg: "Tokeny autoryzacyjne wygasły – wymagane ponowne logowanie.",
                code: "cannot_preathorize_request"
            } as HttpRequestError;
        }

        // 3.4. Dołączenie do request jeżeli mamy AT
        if (ACCESS_TOKEN) {
            // Dodajemy token do nagłówków
            if (config.headers) {
                config.headers.Authorization = `Bearer ${ACCESS_TOKEN}`;
            } else {
                config.headers = {
                    Authorization: `Bearer ${ACCESS_TOKEN}`
                };
            }
        }
    }

    // 4. Rejestracja request & response interceptorów
    AXIOS_INSTANCE.interceptors.request.clear();
    AXIOS_INSTANCE.interceptors.response.clear();

    if (opts.request_interceptors && opts.request_interceptors.length > 0) {
        opts.request_interceptors.forEach(interceptor => {
            AXIOS_INSTANCE.interceptors.request.use(
                interceptor.onFulfilled ?? undefined,
                interceptor.onRejected ?? undefined
            );
        });
    }
    if (opts.response_interceptors && opts.response_interceptors.length > 0) {
        opts.response_interceptors.forEach(interceptor => {
            AXIOS_INSTANCE.interceptors.response.use(
                interceptor.onFulfilled ?? undefined,
                interceptor.onRejected ?? undefined
            );
        });
    }

    // 5. Wykonujemy zapytanie
    try {
        const response = await AXIOS_INSTANCE.request<T>(config);
        return {
            data: response.data
        };
    } catch (error: any) {
        if (axios.isAxiosError(error)) {
            let emsg: string | AddMessageInput = "";

            if (error.response) {
                const ERR_RES = error.response;

                switch (ERR_RES.status) {
                    case 500:
                        emsg = "Nieoczekiwany błąd serwera";
                        break;
                    case 401:
                        if (
                            opts.handle_unauthorized === "logout_user" &&
                            (opts.auth === "force" || opts.auth === "if_available")
                        ) {
                            await logUserOut({
                                alert_msg:
                                    "Twoja sesja wygasła. Zaloguj się ponownie, aby kontynuować korzystanie z aplikacji",
                                alert_type: "error"
                            });
                        }
                        emsg = "Nastąpiło wylogowanie. Brak możliwości wykonania żądania";
                        break;
                    case 403:
                        emsg = "Nie posiadasz uprawnień do tego zasobu";
                        break;
                    case 404:
                        emsg = "Szukany zasób nie został odnaleziony";
                        break;
                    case 429:
                        emsg = "Przekroczono limit żądań w danym okresie czasu";
                        break;
                    default:
                        if (
                            ERR_RES &&
                            ERR_RES.config &&
                            ERR_RES.config.url &&
                            ERR_RES.data &&
                            ERR_RES.data.code
                        ) {
                            const MK = getMessageKeyFromRequestUrl(
                                ERR_RES.config.url,
                                ERR_RES.data.code
                            );

                            if (messageExists(MK)) {
                                const EM = getMessage(MK).split("|");
                                if (EM.length === 1) {
                                    emsg = EM[0];
                                } else if (EM.length === 2) {
                                    emsg = {
                                        type: "error",
                                        msg: EM[1]
                                    };
                                } else if (EM.length === 3) {
                                    emsg = {
                                        type: "error",
                                        msg: EM[1],
                                        duration: !isNaN(parseInt(EM[2])) ? parseInt(EM[2]) : 5000
                                    };
                                } else if (EM.length === 4) {
                                    emsg = {
                                        type: "error",
                                        msg: EM[1],
                                        duration: !isNaN(parseInt(EM[2])) ? parseInt(EM[2]) : 5000
                                    };
                                }
                            }
                        }
                        break;
                }
            } else {
                if (!navigator.onLine) {
                    emsg = {
                        type: "error",
                        msg: "Brak połączenia z&nbsp;Internetem",
                        use_html: true
                    };
                } else {
                    emsg = {
                        msg: "Nie udało&nbsp;się nawiązać połączenia z&nbsp;serwerem",
                        use_html: true,
                        type: "error"
                    };
                }
            }

            if (opts.silent !== true && emsg !== "") {
                displayMessage(
                    typeof emsg === "string"
                        ? {
                              type: "error",
                              msg: emsg
                          }
                        : emsg
                );
            }

            throw {
                type: "axios",
                error: error as AxiosError
            } as HttpRequestError;
        } else {
            throw {
                type: "internal",
                msg: error.message || "Wewnętrzny błąd zapytania",
                code: "internal_request_error"
            } as HttpRequestError;
        }
    }
}

/*##################
### TYPE HELPERS ###
##################*/
export function isApiErrorResponse(obj: any): obj is ApiErrorResponse {
    if (!obj || typeof obj !== "object") return false;

    return (
        obj.success === false &&
        obj.code !== undefined &&
        typeof obj.code === "string" &&
        obj.msg !== undefined &&
        typeof obj.msg === "string"
    );
}

export function isInternalHttpRequestError(err: unknown | any): err is InternalHttpRequestError {
    if (!err || typeof err !== "object") return false;

    return (
        err.type === "internal" &&
        err.msg &&
        typeof err.msg === "string" &&
        err.code !== undefined &&
        typeof err.code === "string"
    );
}
export function isAxiosHttpRequestError(err: unknown | any): err is AxiosHttpRequestError {
    if (!err || typeof err !== "object") return false;

    return err.type === "axios" && err.error && err.error instanceof AxiosError;
}

export function getApiErrorResponseCodeFromError(err: unknown): string | null {
    if (
        isAxiosHttpRequestError(err) &&
        err.error.response &&
        isApiErrorResponse(err.error.response.data)
    ) {
        return err.error.response.data.code;
    }
    return null;
}
